import React from 'react';
import styled, { css } from 'styled-components';
import {
  Text,
  token,
  Popper,
  Popover,
  Paragraph,
  TextButton,
  IconHelpOutline,
  IconCheckmarkCircle,
  IconTrashcanOutline,
} from '@zillow/constellation';
import Spinner from './Spinner';
import FilePreview from './FilePreview';
import { FileUploadStatus } from '../lib/upload-manager';
import { RejectionErrorCode } from '../lib/error';
import { FileUpload, FileUploadGroupType } from '../lib/file-upload';

const iconMixin = css`
  @media (min-width: 501px) {
    &&& svg {
      height: 1rem;
      width: 1rem;
    }
  }
`;

const Container = styled.div`
  --_preview-max-height: 8.75rem;
  width: 100%;
  display: grid;
  grid-template-areas:
    'preview preview'
    'name indicator'
    'description indicator';
  grid-template-columns: 1fr auto;
  grid-template-rows: var(--_preview-max-height) auto auto;
  align-items: center;
  column-gap: 0.5rem;

  @media (max-width: 500px) {
    --_preview-max-height: 5rem;
    grid-template-areas:
      'preview name indicator'
      'preview description indicator';
    grid-template-columns: var(--_preview-max-height) 1fr auto;
    grid-template-rows: repeat(2, minmax(2.5rem, 1fr));
  }
`;

const Preview = styled.div<{ hasError: boolean }>`
  grid-area: preview;
  width: 100%;
  height: 100%;
  max-height: var(--_preview-max-height);
  position: relative;
  border-radius: 3px;
  display: flex;
  background: ${token('colors.gray200')};
  border: ${props =>
    props.hasError
      ? `solid 2px ${token('colors.orange500')(props)}`
      : `solid 1px ${token('colors.gray300')(props)}`};
  box-shadow: 0 2px 2px 0 ${token('colors.gray300')};
`;

const Name = styled(Text)`
  grid-area: name;
  align-self: end;

  @media (min-width: 501px) {
    padding-top: 0.5rem;
  }
`;

const Description = styled.div`
  grid-area: description;
  align-self: start;
  position: relative;

  > button {
    position: absolute;
  }
`;

const Indicator = styled.div`
  grid-area: indicator;
  ${iconMixin}
`;

const IconTextButton = styled(TextButton)`
  ${iconMixin}
`;

const UploadProcessing = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
`;

const UploadOverlay = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  background: ${token('colors.gray200')};
  opacity: 0.9;
`;

export type DeleteFileProps = {
  onDelete: (upload: FileUpload) => void;
};

export type FileItemConsumableProps = {
  acceptDescription?: string;
} & DeleteFileProps;

export type Props = {
  fileUpload: FileUpload;
  className?: string;
} & FileItemConsumableProps;

export default function FileItem({
  fileUpload,
  onDelete,
  acceptDescription,
  className,
}: Props) {
  const isProcessing =
    fileUpload.groupType === FileUploadGroupType.Processing &&
    (fileUpload.status === FileUploadStatus.Pending ||
      fileUpload.status === FileUploadStatus.Uploading);
  const isCompleted = fileUpload.status === FileUploadStatus.Completed;
  const isRejected = fileUpload.status === FileUploadStatus.Rejected;
  const isInvalidType =
    isRejected && fileUpload.errorCode === RejectionErrorCode.InvalidType;
  const hasError = isRejected || fileUpload.status === FileUploadStatus.Failed;

  return (
    <Container className={className}>
      <Preview hasError={hasError}>
        {fileUpload.file && <FilePreview file={fileUpload.file} />}
        {isProcessing && (
          <UploadProcessing>
            <UploadOverlay />
            <Spinner />
          </UploadProcessing>
        )}
      </Preview>
      <Name as="strong" fontType="finePrint" title={fileUpload.file.name}>
        {fileUpload.file.name}
      </Name>
      <Description>
        <Text fontType="finePrint" fontColor={hasError ? 'red600' : undefined}>
          {fileUpload.statusDescription}
        </Text>
        {isInvalidType && acceptDescription && (
          <Popper
            shouldFocusTriggered={false}
            triggered={
              <Popover
                body={
                  <Paragraph>
                    <strong>Supported Types: </strong>
                    {acceptDescription}
                  </Paragraph>
                }
              />
            }
          >
            <IconTextButton
              icon={<IconHelpOutline fontColor="blue400" />}
              iconPosition="end"
              buttonType="primary"
              title="Supported Types"
            />
          </Popper>
        )}
      </Description>
      <Indicator>
        {fileUpload.canDelete ? (
          <IconTextButton
            onClick={() => onDelete(fileUpload)}
            icon={<IconTrashcanOutline fontColor="blue400" />}
            iconPosition="end"
            buttonType="primary"
            title="Delete"
          />
        ) : isCompleted ? (
          <IconCheckmarkCircle fontColor="green400" size="sm" />
        ) : null}
      </Indicator>
    </Container>
  );
}
